import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  FiArrowLeft,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiDownload,
  FiExternalLink,
  FiEye,
  FiFileText,
  FiHelpCircle,
  FiImage,
  FiInfo,
  FiLayers,
  FiLock,
  FiMail,
  FiMapPin,
  FiPackage,
  FiPhone,
  FiPlay,
  FiShield,
  FiTag,
  FiTruck,
  FiUploadCloud,
  FiUserCheck,
  FiVideo,
  FiX,
} from "react-icons/fi";
import { RiMoneyDollarBoxLine } from "react-icons/ri";
import { createPortal } from "react-dom";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import axios from "../../Utils/axiosInstance";
import { useAuth } from "../../Components/Context/AuthContext";
import { s3Upload } from "../../Utils/s3Upload";
import AppLoader from "../../Components/Common/AppLoader";
import { assets } from "../../Assets/assets";
import OTPModal from "../../Components/UserSideComponents/RequirementsPost/Components/OTPModal";
import { downloadVisitPassPDF } from "../../Utils/GenerateVisitPassPdf";

/* ─── Date Formatter ────────────────────────────────────── */
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    : "—";

/* ─── OFM Avatar with Error & Initials Fallback ─────────── */
const OfmAvatar = ({ photo, name }) => {
  const [imgError, setImgError] = useState(false);

  const getInitials = (n) => {
    if (!n) return "OFM";
    const parts = n.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={name || "OFM"}
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold text-xs tracking-wider select-none">
      {getInitials(name)}
    </div>
  );
};

/* ─── Reusable Section Card ─────────────────────────────── */
const SectionCard = ({ title, icon, badge, children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 shadow-xs overflow-hidden ${className}`}>
    <div className="px-4 sm:px-6 py-3.5 sm:py-4 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between flex-wrap gap-2">
      <h3 className="text-xs sm:text-sm font-extrabold text-gray-900 flex items-center gap-2">
        {icon && <span className="text-base text-primaryColor">{icon}</span>}
        {title}
      </h3>
      {badge && <div>{badge}</div>}
    </div>
    <div className="p-4 sm:p-6">{children}</div>
  </div>
);

/* ─── Generic Portal Modal (matching AddressModal structure) ── */
const PortalModal = ({ isOpen, onClose, title, children }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 10);
    } else {
      setAnimate(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      onClick={handleClose}
      className={`fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-xs transition-opacity duration-300 ${animate ? "opacity-100" : "opacity-0"
        }`}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden max-h-[90vh] ${animate ? "scale-100 translate-y-0" : "scale-95 sm:translate-y-4 translate-y-full"
          }`}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center justify-between">
          <h3 className="font-bold text-base sm:text-lg">{title}</h3>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">{children}</div>
      </div>
    </div>,
    document.body
  );
};

/* ─── Image / Video Modal with Download (matching SupplierOrderDetailCard.jsx) ── */
const ImageModal = ({ media, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  if (!media || media.length === 0) return null;

  const goToNext = () =>
    setCurrentIndex((prev) => (prev + 1) % media.length);
  const goToPrev = () =>
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes(".mov") || url?.includes("video");

  const currentUrl = media[currentIndex];

  const handleDownload = (e) => {
    e.stopPropagation();
    const link = document.createElement("a");
    link.href = currentUrl;
    link.download = `quotation-media-${currentIndex + 1}`;
    link.target = "_blank";
    link.rel = "noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top bar controls */}
        <div className="absolute -top-12 left-0 right-0 flex items-center justify-between text-white">
          <span className="text-sm font-medium text-white/80">
            Media {currentIndex + 1} of {media.length}
          </span>
          <div className="flex items-center gap-4">
            <button
              onClick={handleDownload}
              title="Download media"
              className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-semibold transition"
            >
              <FiDownload className="text-sm" /> Download
            </button>
            <button
              onClick={onClose}
              className="text-white/70 hover:text-white transition-colors"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-black">
          {isVideo(currentUrl) ? (
            <video
              src={currentUrl}
              controls
              autoPlay
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          ) : (
            <img
              src={currentUrl}
              alt="Media"
              className="w-full max-h-[75vh] object-contain bg-black"
            />
          )}
        </div>

        {media.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-[#772037] hover:bg-[#CC234F] text-white rounded-full p-3 border border-white/20 transition-all shadow-lg"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

/* ─── Tracking Timeline Component (Mirrors Buyer SelectedQuotationView Delivery UI) ── */
const getTrackingSteps = (status) => [
  {
    label: "Dispatched",
    completed: ["Dispatched", "Delivered"].includes(status),
  },
  {
    label: "Out for Delivery",
    completed: ["Dispatched", "Delivered"].includes(status),
  },
  { label: "Delivered", completed: status === "Delivered" },
];

const TrackingTimelineCard = ({ title, dispatchData, gstInvoice }) => {
  if (!dispatchData) return null;

  const status = dispatchData.status || "Ready_To_Dispatch";
  const isSample = dispatchData.type === "Sample" || title?.toLowerCase().includes("sample");
  const dispatchType = isSample ? "Sample" : "Full_Order";
  const headerTitle = isSample ? "Sample Dispatch Status" : "Full Order Dispatch Status";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden hover:shadow-md hover:border-primaryColor/30 transition-all duration-300 group relative">
      {/* Futuristic Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-primaryColor/5 via-transparent to-subPrimaryColor/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
      {/* Animated Corner Accent */}
      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primaryColor/10 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:w-32 group-hover:h-32 pointer-events-none" />

      {/* HEADER */}
      <div className="px-4 sm:px-5 py-3.5 border-b border-gray-100 bg-gray-50/80 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 relative z-10">
        <div className="flex items-center gap-3">
          {/* Animated Icon */}
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-br from-primaryColor/20 to-subPrimaryColor/20 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">
              {isSample ? "📦" : "🚚"}
            </span>
          </div>
          <div>
            <h4 className="font-bold text-gray-800 text-sm">
              {title || headerTitle}
            </h4>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
              {dispatchType}
            </p>
          </div>
        </div>

        {/* Status Badge */}
        <span
          className={`text-xs font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 self-start sm:self-auto ${status === "Delivered"
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : status === "Dispatched"
              ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-amber-50 text-amber-700 border-amber-200"
            }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full ${status === "Delivered"
              ? "bg-emerald-500"
              : status === "Dispatched"
                ? "bg-blue-500 animate-pulse"
                : "bg-amber-500"
              }`}
          />
          {status === "Dispatched" ? "In Transit" : status}
        </span>
      </div>

      {/* TIMELINE with Enhanced Animations */}
      <div className="px-4 sm:px-5 pt-5 pb-3">
        <div className="relative flex justify-between items-start">
          {getTrackingSteps(status).map((step, idx) => (
            <div
              key={idx}
              className="flex-1 flex flex-col items-center relative"
            >
              {/* Animated Line with Gradient */}
              {idx !== 0 && (
                <div className="absolute left-[-50%] top-3.5 sm:top-4 w-full h-1 overflow-hidden">
                  <div
                    className={`h-full transition-all duration-700 ${step.completed
                      ? "bg-gradient-to-r from-green-400 via-green-500 to-green-600"
                      : "bg-gray-200"
                      }`}
                    style={{
                      animation: step.completed ? "shimmer 2s infinite" : "none",
                    }}
                  />
                </div>
              )}
              {/* Animated Circle with Glow */}
              <div
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-all duration-500 relative ${step.completed
                  ? "bg-gradient-to-br from-green-400 to-green-600 text-white scale-110 shadow-lg shadow-green-500/50"
                  : "bg-gray-200 text-gray-500"
                  }`}
              >
                {step.completed && (
                  <div className="absolute inset-0 rounded-full bg-green-400 animate-ping opacity-20" />
                )}
                {step.completed ? "✓" : ""}
              </div>
              {/* Label */}
              <p
                className={`text-[10px] sm:text-xs mt-2 text-center leading-tight px-1 transition-all duration-500 ${step.completed ? "text-green-600 font-semibold" : "text-gray-400"
                  }`}
              >
                {step.label}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* DETAILS */}
      <div className="px-4 sm:px-5 pb-4 space-y-3 relative z-10">
        {/* Dates Row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-0.5">
          {dispatchData.dispatchedAt && (
            <div className="flex items-center gap-2 py-2 border-t border-gray-50">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
              <span className="text-xs text-gray-400 flex-shrink-0">
                Dispatched:
              </span>
              <span className="text-xs font-semibold text-gray-700 truncate">
                {new Date(dispatchData.dispatchedAt).toLocaleString()}
              </span>
            </div>
          )}
          {dispatchData.deliveredAt && (
            <div className="flex items-center gap-2 py-2 border-t border-gray-50">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
              <span className="text-xs text-gray-400 flex-shrink-0">
                Delivered:
              </span>
              <span className="text-xs font-semibold text-gray-700 truncate">
                {new Date(dispatchData.deliveredAt).toLocaleString()}
              </span>
            </div>
          )}
        </div>

        {/* AWB, Tracking & Invoice Section — Only shows if data exists */}
        {(dispatchData.AWB_Number || dispatchData.trackingUrl || dispatchData.trackingNumber || gstInvoice) && (
          <div className="border-t border-gray-100 pt-3">
            <div className="space-y-2.5">
              {/* GST Tax Invoice */}
              {gstInvoice && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-600 text-sm">📄</span>
                    <span className="text-xs font-semibold text-gray-700">
                      {isSample ? "Sample" : "Full Order"} GST Tax Invoice
                    </span>
                  </div>
                  <a
                    href={gstInvoice}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-bold text-primaryColor hover:underline flex items-center gap-1 transition"
                  >
                    <FiEye /> View PDF
                  </a>
                </div>
              )}

              {/* AWB Number */}
              {(dispatchData.AWB_Number || dispatchData.trackingNumber) && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-blue-50 border border-blue-100">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-600 text-sm">📦</span>
                    <span className="text-xs font-semibold text-blue-700">AWB Number</span>
                  </div>
                  <span className="text-xs font-mono font-bold text-blue-900 bg-white px-3 py-1.5 rounded-lg border border-blue-200">
                    {dispatchData.AWB_Number || dispatchData.trackingNumber}
                  </span>
                </div>
              )}

              {/* Tracking URL */}
              {dispatchData.trackingUrl && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-600 text-sm">🔗</span>
                    <span className="text-xs font-semibold text-emerald-700">Tracking Link</span>
                  </div>
                  <a
                    href={dispatchData.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-bold text-emerald-600 hover:text-emerald-700 hover:underline flex items-center gap-1 transition"
                  >
                    Open Tracking
                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Main Supplier Quotation Details Screen ───────────── */
const SupplierQuotationDetails = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const quotationId = searchParams.get("quotationId");
  const orderId = searchParams.get("orderId");

  const [quotation, setQuotation] = useState(location.state?.quotation || null);
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!quotation || !order);
  const [orderPass, setOrderPass] = useState(null);
  const [passesData, setPassesData] = useState({ samplePass: null, fullOrderPass: null, disputePass: null });
  const [downloadingPass, setDownloadingPass] = useState(false);

  // Dispatch & Logistics State
  const [sampleDispatch, setSampleDispatch] = useState(null);
  const [fullDispatch, setFullDispatch] = useState(null);
  const [dispatchConfirmModal, setDispatchConfirmModal] = useState(false);
  const [dispatchingLoading, setDispatchingLoading] = useState(false);
  const [declarationChecked, setDeclarationChecked] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);

  // Invoices State
  const [localSupplierProforma, setLocalSupplierProforma] = useState(null);
  const [localSampleSupplierProforma, setLocalSampleSupplierProforma] = useState(null);
  const [localSupplierGST, setLocalSupplierGST] = useState(null);
  const [localSampleGST, setLocalSampleGST] = useState(null);

  // Advance Payment State
  const [localAdvanceRequest, setLocalAdvanceRequest] = useState(null);
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [advanceDecision, setAdvanceDecision] = useState(true);
  const [advanceRemarks, setAdvanceRemarks] = useState("");
  const [advanceSubmitting, setAdvanceSubmitting] = useState(false);

  // Invoice Upload Modal State
  const [invoiceUploadModal, setInvoiceUploadModal] = useState({
    isOpen: false,
    type: "full_proforma", // "sample_proforma", "full_gst", "sample_gst"
    title: "",
  });
  const [selectedInvoiceFile, setSelectedInvoiceFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Media Modal (matching SupplierOrderDetailCard.jsx)
  const [mediaModalOpen, setMediaModalOpen] = useState(false);
  const [mediaModalIndex, setMediaModalIndex] = useState(0);
  const [activeMediaList, setActiveMediaList] = useState([]);

  const openProofModal = (proofList, index = 0) => {
    if (!proofList || proofList.length === 0) return;
    setActiveMediaList(proofList);
    setMediaModalIndex(index);
    setMediaModalOpen(true);
  };

  /* ─── Fetch Details ─────────────────────────────────────── */
  const fetchData = useCallback(async () => {
    if (!quotationId && !quotation?._id) return;
    const targetQuotId = quotationId || quotation?._id;

    try {
      setLoading(true);

      // 1. Fetch Quotation
      const quotRes = await axios.get(`/quotation/get-quotation-by-id/${targetQuotId}`);
      if (quotRes.data.success && quotRes.data.data) {
        const qData = quotRes.data.data;
        setQuotation(qData);

        // Sync invoices
        setLocalSupplierProforma(qData.invoice_details?.full_order?.supplierProforma || null);
        setLocalSampleSupplierProforma(qData.invoice_details?.sample?.supplierProforma || null);
        setLocalSupplierGST(qData.invoice_details?.full_order?.supplierGSTInvoice || null);
        setLocalSampleGST(qData.invoice_details?.sample?.supplierGSTInvoice || null);
        setLocalAdvanceRequest(qData.paymentDetails?.fullOrderAdvanceRequest || null);

        // 2. Fetch Order if missing
        const targetOrderId = orderId || qData.orderId?._id || qData.orderId;
        if (targetOrderId) {
          const ordRes = await axios.get(`/order/get-order-by-id/${targetOrderId}`);
          if (ordRes.data.success) {
            setOrder(ordRes.data.data);
          }

          // Fetch Client Visit Pass for order
          try {
            const passRes = await axios.get(`/client-visit-pass/order/${targetOrderId}`);
            if (passRes.data.success) {
              setOrderPass(passRes.data.data || null);
              if (passRes.data.passes) {
                setPassesData(passRes.data.passes);
              }
            }
          } catch (passErr) {
            console.warn("Fetch order pass error:", passErr);
          }
        }

        // 3. Fetch Dispatches
        try {
          const [sampleDispRes, fullDispRes] = await Promise.allSettled([
            axios.get(`/dispatch/${targetQuotId}/Sample`),
            axios.get(`/dispatch/${targetQuotId}/Full_Order`),
          ]);

          if (sampleDispRes.status === "fulfilled" && sampleDispRes.value.data.success) {
            setSampleDispatch(sampleDispRes.value.data.data);
          }
          if (fullDispRes.status === "fulfilled" && fullDispRes.value.data.success) {
            setFullDispatch(fullDispRes.value.data.data);
          }
        } catch (e) {
          console.warn("Error fetching dispatch records:", e);
        }
      }
    } catch (err) {
      console.error("fetchData error:", err);
      toast.error("Failed to load quotation details");
    } finally {
      setLoading(false);
    }
  }, [quotationId, quotation?._id, orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ─── Exact Conditions Matching Mobile Application (SupplierQuotationDetailsView.js) ── */
  const resolvedStatus = quotation?.review?.status || quotation?.status || "Pending";
  const isApproved = resolvedStatus === "Approved" || resolvedStatus === "Selected";
  const isRejected = resolvedStatus?.toLowerCase() === "reject" || resolvedStatus === "Rejected";
  const isOrderClosed = order?.stages === "Closed";

  const isSample = order?.sampleRequsted === "Yes" || quotation?.sampleDetails?.requested === true;
  const currentStageType = order?.stages === "Sampling" ? "Sample" : "Full_Order";

  const impressions = quotation?.ofm_Impressions || [];
  const latestImpression = impressions.length > 0 ? impressions[impressions.length - 1] : null;
  const isFulfilledForCurrentStage =
    latestImpression &&
    latestImpression.status === "Fulfilled" &&
    latestImpression.this_is_for === currentStageType;

  const typeKey = latestImpression?.this_is_for || currentStageType;
  const currentDispatch = typeKey === "Sample" ? sampleDispatch : fullDispatch;

  // LC Verification logic
  const lcStatus = quotation?.paymentDetails?.LC_Status;
  const paymentMethod = quotation?.paymentDetails?.method;
  const isLCVerified = paymentMethod === "Letter_Of_Credit" ? lcStatus === "VERIFIED" : true;
  const supplierLCUrl = quotation?.lcDocument?.supplierFile?.url;

  const isDispatchStageReady =
    (order?.stages === "Order-Fullfillment" ||
      order?.stages === "Sampling" ||
      order?.stages === "Delivery_And_Payment") &&
    isFulfilledForCurrentStage;

  const showDispatchSection = isDispatchStageReady && isLCVerified;
  const showLCVerificationNotice = paymentMethod === "Letter_Of_Credit" && isDispatchStageReady && !isLCVerified;

  // Dispatch Status Flags
  const isDispatched = currentDispatch?.status === "Dispatched";
  const isDelivered = currentDispatch?.status === "Delivered";
  const isFullOrderGSTMissing = typeKey === "Full_Order" && !localSupplierGST;
  const isSampleGSTMissing = typeKey === "Sample" && !localSampleGST;

  // Proforma Invoices: ONLY show when buyer decided to proceed or already uploaded
  const showSampleProforma = quotation?.buyerDecision === "Proceed_With_Sample" || !!localSampleSupplierProforma;
  const showFullOrderProforma = quotation?.buyerDecision === "Proceed_Full_Order" || !!localSupplierProforma;

  const orderUnit =
    quotation?.standardizedUnit ||
    order?.priceAndQuantity?.standardizedUnit ||
    order?.productVariety?.standardizedUNIT ||
    "Kg";

  const quotedAmount = Number(quotation?.pricing?.amount || 0);
  const quotedVolume = Number(quotation?.quantity?.volume || 0);
  const totalEstimatedAmount = quotedAmount * quotedVolume;

  const allMedia = useMemo(() => {
    const imgs = quotation?.referencePhotoAndVideo?.images || quotation?.media?.images || [];
    const vids = quotation?.referencePhotoAndVideo?.videos || quotation?.media?.videos || [];
    return [...imgs, ...vids].filter(Boolean);
  }, [quotation]);

  const isVideo = (url) =>
    url?.includes(".mp4") || url?.includes(".webm") || url?.includes(".mov") || url?.includes("video");

  // Holding Period
  const holdingFromValue = quotation?.holdingPeriod?.from;
  const holdingToValue = quotation?.holdingPeriod?.to;

  const holdingFrom = holdingFromValue ? new Date(holdingFromValue) : null;
  const holdingTo = holdingToValue ? new Date(holdingToValue) : null;

  const calculatedValidityDays =
    holdingFrom && holdingTo
      ? Math.max(0, Math.ceil((holdingTo - holdingFrom) / (1000 * 60 * 60 * 24)) + 1)
      : 0;

  const validityDays = quotation?.holdingPeriod?.validityDays ?? calculatedValidityDays;

  /* ─── Declarations & Actions: Dispatch Confirmation Flow ───── */
  const sampleDeclarationPoints = useMemo(
    () => [
      "Sample being sent for this order is exactly from the same batch used for taking photo and video at the time of providing quotation.",
      "We have sufficient stock available to fulfill this order.",
      "We freeze this offered price till the final order execution date (price will not change due to price fluctuations).",
      "OFM has verified the order and we have given same sample to him as well for future reference.",
    ],
    []
  );

  const fullOrderDeclarationPoints = useMemo(
    () => [
      "Same batch/lot as Sample",
      "Full Specification Compliance",
      "Acceptance of Liability of Deviation",
      "Transport Return Cost Borne by Supplier",
      "Legal consolidator charges payable by supplier in case of dispute",
    ],
    []
  );

  const currentDeclarationPoints =
    typeKey === "Sample" ? sampleDeclarationPoints : fullOrderDeclarationPoints;

  const handleOpenOtpModal = () => {
    if (!declarationChecked) {
      toast.error("Please read and accept all declaration points before continuing.");
      return;
    }
    setDispatchConfirmModal(false);
    setShowOtpModal(true);
  };

  const handleOtpVerified = async () => {
    if (!currentDispatch?._id || !quotation?._id) {
      toast.error("Dispatch record not found. Please contact support.");
      return;
    }

    setDispatchingLoading(true);
    try {
      // 1. Create persistent declaration record in DB
      const declaration_contents = currentDeclarationPoints.map((text) => ({
        content: text,
        isChecked: true,
      }));

      const eSignValue =
        user?.e_signature ||
        `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
        user?.name ||
        user?.email ||
        "Authorized Supplier";

      await axios.post("/declaration", {
        role: "supplier",
        reason:
          typeKey === "Sample"
            ? "Sample Dispatch Confirmation"
            : "Full Order Dispatch Confirmation",
        declaration_is_for: "Single_Quotation",
        declaration_contents,
        quotationId: quotation._id,
        orderId: order?._id,
        e_sign: eSignValue,
        date: new Date().toISOString(),
      });

      // 2. Update Dispatch Status to 'Dispatched'
      const response = await axios.patch(`/dispatch/${currentDispatch._id}`, {
        status: "Dispatched",
        dispatchedBy: user?._id || user?.id,
      });

      if (response.data?.success) {
        toast.success(
          typeKey === "Sample"
            ? "Sample Lot Dispatched Successfully! 🎉"
            : "Full Order Lot Dispatched Successfully! 🎉"
        );
        setShowOtpModal(false);
        setDeclarationChecked(false);
        await fetchData();
      } else {
        toast.error(response.data?.message || "Failed to update dispatch status");
      }
    } catch (err) {
      console.error("handleOtpVerified error:", err);
      toast.error(err?.response?.data?.message || "Failed to complete dispatch process");
    } finally {
      setDispatchingLoading(false);
    }
  };

  /* ─── Actions: Advance Cash Discount Approval ───────────── */
  const handleAdvanceDecisionSubmit = async () => {
    if (!quotation?._id) return;
    setAdvanceSubmitting(true);

    try {
      const res = await axios.patch(
        `/quotation-v2/approve-cash-discount-request/${quotation._id}`,
        {
          approved: advanceDecision,
          remarks: advanceRemarks.trim() || null,
        }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Cash discount response submitted!");
        const updated = res.data?.data?.fullOrderAdvanceRequest;
        setLocalAdvanceRequest(updated || ((prev) => ({
          ...prev,
          status: advanceDecision ? "Approved" : "Rejected",
          supplierApproval: {
            status: advanceDecision ? "Approved" : "Rejected",
            remarks: advanceRemarks.trim() || null,
            respondedAt: new Date().toISOString(),
          },
        })));
        setAdvanceModalOpen(false);
      } else {
        toast.error(res.data.message || "Failed to update advance request");
      }
    } catch (err) {
      console.error("handleAdvanceDecisionSubmit error:", err);
      toast.error(err?.response?.data?.message || "Failed to submit decision");
    } finally {
      setAdvanceSubmitting(false);
    }
  };

  /* ─── Actions: Invoice Upload ───────────────────────────── */
  const openInvoiceUploadModal = (type, title) => {
    setSelectedInvoiceFile(null);
    setInvoiceUploadModal({ isOpen: true, type, title });
  };

  const handleInvoiceUploadSubmit = async () => {
    if (!selectedInvoiceFile || !quotation?._id) {
      toast.error("Please select a PDF file");
      return;
    }

    setUploadingInvoice(true);
    try {
      const uploadRes = await s3Upload(selectedInvoiceFile, "invoices/supplier");
      const fileUrl = uploadRes.url;

      let payload = {};
      if (invoiceUploadModal.type === "full_proforma") {
        payload = { invoice_details: { full_order: { supplierProforma: fileUrl } } };
      } else if (invoiceUploadModal.type === "sample_proforma") {
        payload = { invoice_details: { sample: { supplierProforma: fileUrl } } };
      } else if (invoiceUploadModal.type === "full_gst") {
        payload = { invoice_details: { full_order: { supplierGSTInvoice: fileUrl } } };
      } else if (invoiceUploadModal.type === "sample_gst") {
        payload = { invoice_details: { sample: { supplierGSTInvoice: fileUrl } } };
      }

      const res = await axios.patch(`/quotation/update-quotation/${quotation._id}`, payload);
      if (res.data.success) {
        toast.success(`${invoiceUploadModal.title} uploaded successfully! 🎉`);

        if (invoiceUploadModal.type === "full_proforma") setLocalSupplierProforma(fileUrl);
        if (invoiceUploadModal.type === "sample_proforma") setLocalSampleSupplierProforma(fileUrl);
        if (invoiceUploadModal.type === "full_gst") setLocalSupplierGST(fileUrl);
        if (invoiceUploadModal.type === "sample_gst") setLocalSampleGST(fileUrl);

        setInvoiceUploadModal({ isOpen: false, type: "", title: "" });
        setSelectedInvoiceFile(null);
      } else {
        toast.error(res.data.message || "Failed to update quotation");
      }
    } catch (err) {
      console.error("handleInvoiceUploadSubmit error:", err);
      toast.error(err?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setUploadingInvoice(false);
    }
  };

  if (loading) {
    return <AppLoader message="Loading Quotation Details..." />;
  }

  if (!quotation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl border border-gray-200 text-center max-w-md shadow-xs">
          <p className="text-gray-700 font-bold mb-3">Quotation not found or inaccessible.</p>
          <button
            onClick={() => navigate("/supplier-orders")}
            className="px-4 py-2 bg-primaryColor text-white font-semibold rounded-xl text-xs"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="w-full bg-[#FDFBFB] min-h-screen pb-24 md:pb-16">
      {/* ── HERO BANNER ──────────────────────────────────────────────────────── */}
      <div
        className="w-full h-[140px] sm:h-[200px] md:h-[240px] flex items-center justify-center bg-no-repeat bg-center bg-cover relative"
        style={{ backgroundImage: `url(${assets.requirementsbanner || "/assets/banner.png"})` }}
      >
        <div className="relative z-10 text-center px-4">
          <h1 className="text-subPrimaryColor text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-semibold text-center">
            Quotation Details
          </h1>
          <p className="text-subPrimaryColor text-xs sm:text-sm font-medium mt-1">
            Quote Ref: <strong className="text-subPrimaryColor">{quotation.quotationUniqueNumber || `#${quotation._id?.slice(-6)}`}</strong> • Submitted on {fmtDate(quotation.createdAt)}
          </p>
        </div>
      </div>

      {/* ── MAIN CONTAINER (2/3-COLUMN LAYOUT) ────────────────────────────────── */}
      <div className="2xl:max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {/* Navigation & Header Status Bar */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-gray-200 shadow-xs">
          <button
            type="button"
            onClick={() => navigate(order?._id ? `/supplier-quotations?orderId=${order._id}` : "/supplier-orders")}
            className="flex items-center gap-2 text-xs sm:text-sm font-bold text-gray-700 hover:text-primaryColor transition"
          >
            <FiArrowLeft className="text-base" /> Back to Quotations List
          </button>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Review Status Badge */}
            <span
              className={`text-xs font-bold px-3 py-1 rounded-full border ${isApproved
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : isRejected
                  ? "bg-red-50 text-red-700 border-red-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
            >
              Review: {resolvedStatus}
            </span>

            {/* Buyer Decision Badge */}
            {quotation.buyerDecision && (
              <span className="text-xs font-bold px-3 py-1 rounded-full bg-primaryColor/10 text-primaryColor border border-primaryColor/20">
                Buyer Decision: {quotation.buyerDecision.replace(/_/g, " ")}
              </span>
            )}

            {/* Order Stage Badge */}
            {order?.stages && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                Stage: {order.stages.replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>

        {/* ── ORDER CLOSED WARNING BANNER ── */}
        {isOrderClosed && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-2xl flex items-start gap-3 text-xs text-red-800">
            <FiLock className="text-base flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-bold text-sm text-red-900">Order Closed</p>
              <p className="mt-0.5 leading-relaxed text-red-700">
                This order has been completed or closed. Actions such as dispatch and document modifications are disabled.
              </p>
            </div>
          </div>
        )}

        {/* ── 2-COLUMN GRID (8 / 4) ─────────────────────────────────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* 1. MAIN CONTENT COLUMN (lg:col-span-8)                             */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-8 space-y-6">
            {/* ─── 1. ORDER REQUIREMENT OVERVIEW (MATCHING APPLICATION) ──────── */}
            {order && (
              <SectionCard
                title="Target Order Requirement"
                icon={<FiPackage />}
                badge={
                  <span className="text-[11px] font-bold text-primaryColor bg-primaryColor/10 px-2.5 py-1 rounded-lg">
                    Order Ref: {order.orderNumber || `#${order._id?.slice(-6)}`}
                  </span>
                }
              >
                <div className="space-y-4">
                  {/* Product Thumbnail & Hierarchy */}
                  <div className="flex items-center gap-4 p-3.5 rounded-2xl bg-gray-50 border border-gray-200/80">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl bg-white border border-gray-200 overflow-hidden flex-shrink-0 flex items-center justify-center shadow-2xs">
                      {order.productVariety?.img || order.product?.img ? (
                        <img
                          src={order.productVariety?.img || order.product?.img}
                          alt="Product"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-2xl">📦</span>
                      )}
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-primaryColor bg-primaryColor/10 px-2 py-0.5 rounded">
                          {order.category?.name || "Category"}
                        </span>
                        {order.subCategory?.name && (
                          <span className="text-[10px] font-semibold text-gray-500 bg-gray-200/70 px-2 py-0.5 rounded">
                            {order.subCategory.name}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm sm:text-base font-extrabold text-gray-900 mt-1 truncate">
                        {order.product?.name || "Product Name"}
                      </h4>
                      <p className="text-xs font-semibold text-gray-600 truncate mt-0.5">
                        Variety: <span className="text-gray-900">{order.productVariety?.varietyName || "Standard Variety"}</span>
                        {order.productVariety?.grade && ` • Grade: ${order.productVariety.grade}`}
                      </p>
                    </div>
                  </div>

                  {/* Buyer Requirement Key Metrics */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Buyer Required MOQ</p>
                      <p className="font-extrabold text-gray-900 text-sm mt-0.5">
                        {order.priceAndQuantity?.MOQ?.toLocaleString() || "—"} {orderUnit}
                      </p>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Target Price Range</p>
                      <p className="font-extrabold text-[#772037] text-sm mt-0.5">
                        ₹{order.priceAndQuantity?.expectedPriceRange?.min?.toLocaleString() || 0} – ₹{order.priceAndQuantity?.expectedPriceRange?.max?.toLocaleString() || 0}
                      </p>
                    </div>

                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 col-span-2 sm:col-span-1">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tight">Delivery Window</p>
                      <p className="font-bold text-gray-700 text-xs mt-0.5">
                        {order.deliveryPeriod?.fromDate
                          ? `${fmtDate(order.deliveryPeriod.fromDate)} – ${fmtDate(order.deliveryPeriod.toDate)}`
                          : "Immediate Dispatch"}
                      </p>
                    </div>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* ─── 2. QUOTATION COMMERCIALS & OFFER DETAILS (PRICE & QUANTITY) ── */}
            <SectionCard
              title={"Full Order Details"}
              icon={<FiDollarSign />}
              badge={
                <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Confirmed Proposal
                </span>
              }
            >
              <div className="space-y-4">
                {/* 3-Column Highlights */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-gradient-to-br from-[#772037]/6 to-[#CC234F]/6 border border-[#772037]/20 rounded-xl p-3.5 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Quoted Unit Price</p>
                    <p className="text-xl sm:text-2xl font-extrabold text-[#772037]">
                      ₹ {quotedAmount.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">per {orderUnit}</p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3.5 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Supplied Quantity</p>
                    <p className="text-xl sm:text-2xl font-extrabold text-gray-800">
                      {quotedVolume.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">{orderUnit}</p>
                  </div>

                  {/* <div className="bg-emerald-50/80 border border-emerald-200 rounded-xl p-3.5 text-center">
                    <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider mb-1">Estimated Total</p>
                    <p className="text-xl sm:text-2xl font-extrabold text-emerald-800">
                      ₹ {Math.round(totalEstimatedAmount).toLocaleString("en-IN")}
                    </p>
                    <p className="text-xs text-emerald-600 mt-0.5">Excluding Tax & Logistics</p>
                  </div> */}
                </div>

                {/* Additional Spec Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 pt-2 border-t border-gray-100 text-xs">
                  <div className="p-2.5 bg-gray-50 rounded-xl">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase">Batch / Lot No</p>
                    <p className="font-extrabold text-gray-800 mt-0.5 truncate">
                      {quotation.batch_Lot_Number || quotation.batchNumber || "Standard"}
                    </p>
                  </div>

                  <div className="p-2.5 bg-gray-50 rounded-xl">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase">Unit Weight</p>
                    <p className="font-extrabold text-gray-800 mt-0.5">
                      {quotation.packSize?.weight ? `${quotation.packSize.weight} ${quotation.packSize?.unit || quotation.standardizedUnit || orderUnit}` : "—"}
                    </p>
                  </div>

                  <div className="p-2.5 bg-gray-50 rounded-xl col-span-2">
                    <p className="text-[10px] text-gray-400 font-semibold uppercase">Pack Dimensions (L × B × H)</p>
                    <p className="font-extrabold text-gray-800 mt-0.5">
                      {quotation.packSize?.dimensions?.length
                        ? `${quotation.packSize.dimensions.length} × ${quotation.packSize.dimensions.breadth} × ${quotation.packSize.dimensions.height} ${quotation.packSize?.dimensions?.measured || "cm"}`
                        : "Standard Packaging"}
                    </p>
                  </div>
                </div>
              </div>
            </SectionCard>

            {/* ─── 3. HOLDING PERIOD & VALIDITY (MATCHING APPLICATION) ───────── */}
            <SectionCard
              title="Stock Holding Period & Validity"
              icon={<FiClock />}
              badge={
                validityDays > 0 ? (
                  <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                    {validityDays} Days Validity
                  </span>
                ) : null
              }
            >
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-200 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Holding From Date</p>
                    <p className="text-sm sm:text-base font-extrabold text-gray-800 mt-1">
                      {fmtDate(holdingFrom)}
                    </p>
                  </div>
                  <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-200 text-center">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Holding To Date</p>
                    <p className="text-sm sm:text-base font-extrabold text-gray-800 mt-1">
                      {fmtDate(holdingTo)}
                    </p>
                  </div>
                </div>

                <p className="text-[11px] text-gray-500 text-center">
                  You have committed to hold this stocklot ready for dispatch until the holding validity date expires.
                </p>
              </div>
            </SectionCard>

            {/* ─── 4. LETTER OF CREDIT (LC) ─────────────────────────────────── */}
            {paymentMethod === "Letter_Of_Credit" && supplierLCUrl && (
              <SectionCard
                title="Letter of Credit (LC)"
                icon={<FiShield />}
                badge={
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                    Verified & Shared
                  </span>
                }
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600">Official LC Document:</span>
                  <a
                    href={supplierLCUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primaryColor font-bold hover:underline"
                  >
                    <FiFileText /> View LC Document
                  </a>
                </div>
              </SectionCard>
            )}

            {/* ─── 5. LC VERIFICATION PENDING NOTICE ────────────────────────── */}
            {showLCVerificationNotice && (
              <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1.5">
                <p className="font-bold flex items-center gap-2 text-amber-950">
                  <FiClock className="text-amber-600" /> Dispatch Waiting for LC Verification
                </p>
                <p className="text-amber-800 leading-relaxed">
                  The quotation inspection is fulfilled, but dispatch is waiting for the Letter of Credit to be verified by admin. You can continue with necessary preparations in the meantime.
                </p>
              </div>
            )}

            {/* ─── 6. ADVANCE PAYMENT / CASH DISCOUNT REQUEST ───────────────── */}
            {localAdvanceRequest?.status && localAdvanceRequest.status !== "Not_Requested" && (
              <SectionCard
                title="Advance Payment Request"
                icon={<FiDollarSign />}
                badge={
                  <span
                    className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${localAdvanceRequest.status === "Approved"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : localAdvanceRequest.status === "Rejected"
                        ? "bg-red-50 text-red-700 border-red-200"
                        : "bg-amber-50 text-amber-700 border-amber-200"
                      }`}
                  >
                    {localAdvanceRequest.status}
                  </span>
                }
              >
                <div className="space-y-4 text-xs">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-gray-400 font-bold text-[10px] uppercase">Total Order Value</p>
                      <p className="font-extrabold text-gray-800 text-sm mt-0.5">
                        ₹ {localAdvanceRequest.totalQuotationAmount?.toLocaleString() || "—"}
                      </p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-gray-400 font-bold text-[10px] uppercase">Cash Discount</p>
                      <p className="font-extrabold text-primaryColor text-sm mt-0.5">
                        ₹ {localAdvanceRequest.cashDiscountAmount?.toLocaleString() || "—"}
                      </p>
                    </div>
                    <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200 col-span-2 sm:col-span-1">
                      <p className="text-emerald-700 font-bold text-[10px] uppercase">Advance Payable</p>
                      <p className="font-extrabold text-emerald-800 text-sm mt-0.5">
                        ₹ {localAdvanceRequest.advancePaymentAmount?.toLocaleString() || "—"}
                      </p>
                    </div>
                  </div>

                  {localAdvanceRequest.adminRemarks && (
                    <div className="p-3 bg-gray-50 rounded-xl">
                      <p className="font-bold text-gray-700">Admin Remarks:</p>
                      <p className="text-gray-600 mt-0.5">{localAdvanceRequest.adminRemarks}</p>
                    </div>
                  )}

                  {/* Notice if no proof uploaded yet */}
                  {localAdvanceRequest.status === "Pending" && (!localAdvanceRequest.paymentProof || localAdvanceRequest.paymentProof.length === 0) && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 flex items-start gap-2">
                      <FiClock className="text-amber-600 flex-shrink-0 mt-0.5 text-base" />
                      <span>
                        Please review the discussed cash discount amount and confirm your acceptance below. Payment proof can be uploaded by admin upon payment disbursement.
                      </span>
                    </div>
                  )}

                  {localAdvanceRequest.status === "Approved" && (!localAdvanceRequest.paymentProof || localAdvanceRequest.paymentProof.length === 0) && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 flex items-start gap-2">
                      <FiCheckCircle className="text-emerald-600 flex-shrink-0 mt-0.5 text-base" />
                      <span>
                        Cash discount terms accepted! Awaiting payment disbursement and proof upload from admin.
                      </span>
                    </div>
                  )}

                  {localAdvanceRequest.status === "Approved" && localAdvanceRequest.paymentProof?.length > 0 && (
                    <div className="p-2.5 bg-emerald-50/80 border border-emerald-200 rounded-xl text-emerald-800 flex items-center gap-2">
                      <FiCheckCircle className="text-emerald-600 flex-shrink-0 text-sm" />
                      <span className="font-semibold text-xs">
                        Advance payment disbursed and proof uploaded by admin.
                      </span>
                    </div>
                  )}

                  {/* Proofs */}
                  {localAdvanceRequest.paymentProof?.length > 0 && (
                    <div className="space-y-1.5">
                      <p className="font-bold text-gray-700">Payment Proofs from Admin:</p>
                      <div className="flex gap-2 flex-wrap">
                        {localAdvanceRequest.paymentProof.map((proofUrl, i) => (
                          <a
                            key={i}
                            href={proofUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primaryColor/10 text-primaryColor font-bold hover:bg-primaryColor/20 transition"
                          >
                            <FiFileText /> Proof Document #{i + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Supplier Action Trigger */}
                  {localAdvanceRequest.status === "Pending" && (
                    <div className="pt-2 border-t border-gray-100 flex gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setAdvanceDecision(false);
                          setAdvanceRemarks("");
                          setAdvanceModalOpen(true);
                        }}
                        className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-red-50 text-red-600 hover:text-red-700 border border-gray-300 hover:border-red-200 font-bold transition"
                      >
                        ✕ Decline Request
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAdvanceDecision(true);
                          setAdvanceRemarks("");
                          setAdvanceModalOpen(true);
                        }}
                        className="flex-1 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold shadow-sm transition"
                      >
                        ✓ Accept Cash Discount
                      </button>
                    </div>
                  )}

                  {localAdvanceRequest.supplierApproval?.status && (
                    <div className="p-2.5 rounded-xl bg-gray-50 border border-gray-200 flex items-center justify-between">
                      <span className="text-gray-500">Your Response:</span>
                      <span className="font-bold text-gray-800">
                        {localAdvanceRequest.supplierApproval.status} • {fmtDate(localAdvanceRequest.supplierApproval.respondedAt)}
                      </span>
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* ─── 7. READY TO DISPATCH FULFILLMENT SECTION ──────────────────── */}
            {showDispatchSection && !isOrderClosed && !isDispatched && !isDelivered && (
              <div className="bg-gradient-to-br from-white to-emerald-50/40 border-2 border-emerald-500/40 rounded-2xl p-4 sm:p-6 shadow-sm space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-emerald-100">
                  <h3 className="text-sm sm:text-base font-extrabold text-emerald-900 flex items-center gap-2">
                    <FiTruck className="text-emerald-600" /> {typeKey === "Sample" ? "Sample Order" : "Full Order"} — Ready To Dispatch
                  </h3>
                  <span
                    className={`text-xs font-bold px-3 py-0.5 rounded-full border ${isDelivered
                      ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                      : isDispatched
                        ? "bg-blue-100 text-blue-800 border-blue-300"
                        : "bg-amber-100 text-amber-800 border-amber-300"
                      }`}
                  >
                    {isDelivered ? "Delivered" : isDispatched ? "Dispatched" : "Ready To Dispatch"}
                  </span>
                </div>

                {/* GST Invoice Info inside Dispatch section */}
                {typeKey === "Full_Order" && localSupplierGST && (
                  <div className="p-3 bg-white rounded-xl border border-gray-200 flex items-center justify-between text-xs">
                    <span className="font-bold text-gray-700">Full Order GST Invoice:</span>
                    <a
                      href={localSupplierGST}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primaryColor font-bold hover:underline flex items-center gap-1"
                    >
                      <FiEye /> View PDF
                    </a>
                  </div>
                )}

                {typeKey === "Sample" && localSampleGST && (
                  <div className="p-3 bg-white rounded-xl border border-gray-200 flex items-center justify-between text-xs">
                    <span className="font-bold text-gray-700">Sample GST Invoice:</span>
                    <a
                      href={localSampleGST}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primaryColor font-bold hover:underline flex items-center gap-1"
                    >
                      <FiEye /> View PDF
                    </a>
                  </div>
                )}

                {/* GST Invoice Reminder & Upload if Missing */}
                {(isFullOrderGSTMissing || isSampleGSTMissing) && !isDispatched && !isDelivered && (
                  <div className="p-4 bg-amber-50/90 border border-amber-200 rounded-xl space-y-3">
                    <div className="text-xs text-amber-900">
                      <p className="font-bold">⚠️ GST Invoice Required</p>
                      <p className="mt-0.5 text-amber-800">
                        Please upload your GST tax invoice for this {typeKey === "Sample" ? "sample" : "full"} order before dispatching.
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        openInvoiceUploadModal(
                          typeKey === "Sample" ? "sample_gst" : "full_gst",
                          typeKey === "Sample" ? "Sample GST Invoice" : "Full Order GST Tax Invoice"
                        )
                      }
                      className="py-2 px-4 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition flex items-center gap-1.5 shadow-sm"
                    >
                      <FiUploadCloud /> Upload GST Invoice
                    </button>
                  </div>
                )}

                {/* Dispatch Button */}
                <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-emerald-100">
                  <p className="text-xs text-gray-500">
                    {isDelivered
                      ? "This lot has been successfully delivered and confirmed by buyer."
                      : isDispatched
                        ? "Lot is in transit. Tracking details are available below."
                        : "Inspection is complete. Click below to sign dispatch declarations and generate shipment lot."}
                  </p>
                  <button
                    type="button"
                    disabled={isDispatched || isDelivered || isFullOrderGSTMissing || isSampleGSTMissing}
                    onClick={() => setDispatchConfirmModal(true)}
                    className={`w-full sm:w-auto px-6 py-2.5 rounded-xl font-bold text-xs shadow-sm transition flex items-center justify-center gap-2 ${isDispatched || isDelivered || isFullOrderGSTMissing || isSampleGSTMissing
                      ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20"
                      }`}
                  >
                    <FiTruck className="text-sm" />
                    {isDelivered ? "Delivered" : isDispatched ? "Dispatched" : "Dispatch Lot"}
                  </button>
                </div>
              </div>
            )}

            {/* ─── 8. DELIVERY TRACKING TIMELINES ───────────────────────────── */}
            {sampleDispatch && ["Dispatched", "Delivered"].includes(sampleDispatch.status) && (
              <TrackingTimelineCard
                title="Sample Dispatch Status"
                dispatchData={sampleDispatch}
                gstInvoice={localSampleGST}
              />
            )}
            {fullDispatch && ["Dispatched", "Delivered"].includes(fullDispatch.status) && (
              <TrackingTimelineCard
                title="Full Order Dispatch Status"
                dispatchData={fullDispatch}
                gstInvoice={localSupplierGST}
              />
            )}

            {/* ─── 9. PROFORMA INVOICES (ONLY WHEN BUYER HAS DECIDED TO PROCEED) ── */}
            {(!isOrderClosed && (showFullOrderProforma || showSampleProforma)) && (
              <SectionCard
                title="Proforma Invoice Documentation"
                icon={<FiFileText />}
                badge={<span className="text-[11px] text-gray-400">PDF Format</span>}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Full Order Proforma */}
                  {showFullOrderProforma && (
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-extrabold text-xs text-gray-800">Full Order Proforma Invoice</span>
                        {localSupplierProforma ? (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Uploaded</span>
                        ) : (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                        )}
                      </div>

                      {localSupplierProforma ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={localSupplierProforma}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 py-2 px-3 rounded-lg bg-primaryColor text-white text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-primaryColor/90 transition shadow-xs"
                          >
                            <FiEye /> View PDF
                          </a>
                          {!isOrderClosed && (
                            <button
                              type="button"
                              onClick={() => openInvoiceUploadModal("full_proforma", "Full Order Proforma Invoice")}
                              className="py-2 px-3 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-bold hover:border-primaryColor transition"
                            >
                              Replace
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={isOrderClosed}
                          onClick={() => openInvoiceUploadModal("full_proforma", "Full Order Proforma Invoice")}
                          className="w-full py-2.5 rounded-lg border-2 border-dashed border-primaryColor/40 text-primaryColor hover:bg-primaryColor/5 font-bold text-xs transition flex items-center justify-center gap-1.5"
                        >
                          <FiUploadCloud /> Upload Proforma Invoice
                        </button>
                      )}
                    </div>
                  )}

                  {/* Sample Proforma */}
                  {showSampleProforma && (
                    <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="font-extrabold text-xs text-gray-800">Sample Proforma Invoice</span>
                        {localSampleSupplierProforma ? (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Uploaded</span>
                        ) : (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Pending</span>
                        )}
                      </div>

                      {localSampleSupplierProforma ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={localSampleSupplierProforma}
                            target="_blank"
                            rel="noreferrer"
                            className="flex-1 py-2 px-3 rounded-lg bg-primaryColor text-white text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-primaryColor/90 transition"
                          >
                            <FiEye /> View PDF
                          </a>
                          {!isOrderClosed && (
                            <button
                              type="button"
                              onClick={() => openInvoiceUploadModal("sample_proforma", "Sample Proforma Invoice")}
                              className="py-2 px-3 rounded-lg bg-white border border-gray-300 text-gray-700 text-xs font-bold hover:border-primaryColor transition"
                            >
                              Replace
                            </button>
                          )}
                        </div>
                      ) : (
                        <button
                          type="button"
                          disabled={isOrderClosed}
                          onClick={() => openInvoiceUploadModal("sample_proforma", "Sample Proforma Invoice")}
                          className="w-full py-2.5 rounded-lg border-2 border-dashed border-primaryColor/40 text-primaryColor hover:bg-primaryColor/5 font-bold text-xs transition flex items-center justify-center gap-1.5"
                        >
                          <FiUploadCloud /> Upload Sample Proforma
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* ─── 10. ASSIGNED OFM (Order Fulfillment Managers) ────────────── */}
            {(quotation.assignedOFMs?.length > 0 || orderPass || passesData.samplePass || passesData.fullOrderPass) && (
              <SectionCard
                title="Assigned Inspection Team (OFM)"
                icon={<FiUserCheck />}
                badge={
                  (orderPass || passesData.samplePass || passesData.fullOrderPass) ? (
                    <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200">
                      Visit Pass Issued
                    </span>
                  ) : null
                }
              >
                <div className="space-y-4">
                  {/* Separate Visit Pass Cards: Full Order & Sample */}
                  {/* Separate Visit Pass Cards: Full Order & Sample */}
                  {(() => {
                    const fullOrderPassDoc =
                      passesData?.fullOrderPass ||
                      (orderPass?.passType === "Full_Order" || orderPass?.passType === "Full Order" ? orderPass : null);
                    const samplePassDoc =
                      passesData?.samplePass ||
                      (orderPass?.passType === "Sample" ? orderPass : null);

                    const shouldShowPassSection = Boolean(fullOrderPassDoc || samplePassDoc || orderPass);
                    if (!shouldShowPassSection) return null;

                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Full Order Visit Pass Card */}
                        {(fullOrderPassDoc || !isSample) && (
                          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="text-base">🚚</span>
                                <span className="font-extrabold text-xs text-gray-800">Full Order Client Visit Pass</span>
                              </div>
                              {fullOrderPassDoc ? (
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                              ) : (
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Not Issued</span>
                              )}
                            </div>

                            {fullOrderPassDoc ? (
                              <div className="space-y-2.5">
                                {(() => {
                                  const pass = fullOrderPassDoc;
                                  return (
                                    <>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-extrabold text-gray-900 font-mono">{pass.passNumber || "CVP-ACTIVE"}</span>
                                        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                                          🚚 Full Order Stage
                                        </span>
                                      </div>

                                      {/* Assigned OFM Profile Box */}
                                      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-blue-50/70 border border-blue-100">
                                        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-xs shadow-xs overflow-hidden border border-blue-200">
                                          <OfmAvatar
                                            photo={
                                              pass.ofmDetails?.photo ||
                                              pass.ofmId?.profilePic ||
                                              pass.ofmId?.identityVerification?.aadhaar?.photoUrl
                                            }
                                            name={
                                              pass.ofmDetails?.name ||
                                              (pass.ofmId
                                                ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                                                : "OFM Executive")
                                            }
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[9px] font-extrabold text-blue-800 uppercase tracking-wider">
                                            Assigned OFM Executive
                                          </p>
                                          <p className="text-xs font-bold text-slate-900 truncate">
                                            {pass.ofmDetails?.name ||
                                              (pass.ofmId
                                                ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                                                : "OFM Executive")}
                                          </p>
                                          <p className="text-[11px] text-slate-600">
                                            📞 {pass.ofmDetails?.phone || pass.ofmId?.phone || "Contact on arrival"}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-[11px] text-gray-600 space-y-1 bg-white p-2.5 rounded-lg border border-gray-100">
                                        <div><span className="font-bold text-gray-700">Validity:</span> {fmtDate(pass.validity?.fromDate)} – {fmtDate(pass.validity?.toDate)}</div>
                                        <div><span className="font-bold text-gray-700">Assignment:</span> {pass.assignmentNumber || "Order Verification"}</div>
                                      </div>
                                      <button
                                        type="button"
                                        disabled={downloadingPass}
                                        onClick={async () => {
                                          try {
                                            setDownloadingPass(true);
                                            await downloadVisitPassPDF(pass);
                                            toast.success("Full Order Visit Pass PDF downloaded!");
                                          } catch (e) {
                                            toast.error("Failed to download PDF");
                                          } finally {
                                            setDownloadingPass(false);
                                          }
                                        }}
                                        className="w-full py-2 px-3 rounded-lg bg-primaryColor hover:bg-primaryColor/90 text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-xs"
                                      >
                                        <FiDownload />
                                        {downloadingPass ? "Generating PDF..." : "Download Visit Pass PDF"}
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 italic py-2">No visit pass issued for full order stage yet.</p>
                            )}
                          </div>
                        )}

                        {/* Sample Visit Pass Card */}
                        {(isSample || samplePassDoc) && (
                          <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 space-y-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5">
                                <span className="text-base">📦</span>
                                <span className="font-extrabold text-xs text-gray-800">Sample Client Visit Pass</span>
                              </div>
                              {samplePassDoc ? (
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Active</span>
                              ) : (
                                <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Not Issued</span>
                              )}
                            </div>

                            {samplePassDoc ? (
                              <div className="space-y-2.5">
                                {(() => {
                                  const pass = samplePassDoc;
                                  return (
                                    <>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs font-extrabold text-gray-900 font-mono">{pass.passNumber || "CVP-ACTIVE"}</span>
                                        <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">
                                          📦 Sample Stage
                                        </span>
                                      </div>

                                      {/* Assigned OFM Profile Box */}
                                      <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-blue-50/70 border border-blue-100">
                                        <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-xs shadow-xs overflow-hidden border border-blue-200">
                                          <OfmAvatar
                                            photo={
                                              pass.ofmDetails?.photo ||
                                              pass.ofmId?.profilePic ||
                                              pass.ofmId?.identityVerification?.aadhaar?.photoUrl
                                            }
                                            name={
                                              pass.ofmDetails?.name ||
                                              (pass.ofmId
                                                ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                                                : "OFM Executive")
                                            }
                                          />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-[9px] font-extrabold text-blue-800 uppercase tracking-wider">
                                            Assigned OFM Executive
                                          </p>
                                          <p className="text-xs font-bold text-slate-900 truncate">
                                            {pass.ofmDetails?.name ||
                                              (pass.ofmId
                                                ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                                                : "OFM Executive")}
                                          </p>
                                          <p className="text-[11px] text-slate-600">
                                            📞 {pass.ofmDetails?.phone || pass.ofmId?.phone || "Contact on arrival"}
                                          </p>
                                        </div>
                                      </div>

                                      <div className="text-[11px] text-gray-600 space-y-1 bg-white p-2.5 rounded-lg border border-gray-100">
                                        <div><span className="font-bold text-gray-700">Validity:</span> {fmtDate(pass.validity?.fromDate)} – {fmtDate(pass.validity?.toDate)}</div>
                                        <div><span className="font-bold text-gray-700">Assignment:</span> {pass.assignmentNumber || "Sample Verification"}</div>
                                      </div>
                                      <button
                                        type="button"
                                        disabled={downloadingPass}
                                        onClick={async () => {
                                          try {
                                            setDownloadingPass(true);
                                            await downloadVisitPassPDF(pass);
                                            toast.success("Sample Stage Visit Pass PDF downloaded!");
                                          } catch (e) {
                                            toast.error("Failed to download PDF");
                                          } finally {
                                            setDownloadingPass(false);
                                          }
                                        }}
                                        className="w-full py-2 px-3 rounded-lg bg-primaryColor hover:bg-primaryColor/90 text-white text-xs font-bold transition flex items-center justify-center gap-1.5 shadow-xs"
                                      >
                                        <FiDownload />
                                        {downloadingPass ? "Generating PDF..." : "Download Visit Pass PDF"}
                                      </button>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <p className="text-xs text-gray-400 italic py-2">No visit pass issued for sample stage yet.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Assigned OFM List */}
                  {quotation.assignedOFMs?.map((item, idx) => {
                    const ofm = item.ofmId || {};
                    const ofmName = `${ofm.firstName || "Inspection"} ${ofm.lastName || "Officer"}`.trim();
                    const ofmPhoto = ofm.profilePic || ofm.identityVerification?.aadhaar?.photoUrl;

                    return (
                      <div key={item._id || idx} className="p-3.5 rounded-xl bg-gray-50 border border-gray-200 space-y-3 text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-xs shadow-xs overflow-hidden border border-blue-200">
                              <OfmAvatar photo={ofmPhoto} name={ofmName} />
                            </div>
                            <div>
                              <p className="font-extrabold text-gray-900 text-sm">{ofmName}</p>
                              <p className="text-[10px] text-gray-500 font-semibold">Order Fulfillment Manager</p>
                            </div>
                          </div>
                          <span
                            className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${item.isAllowed
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-red-50 text-red-700 border-red-200"
                              }`}
                          >
                            {item.isAllowed ? "Inspection Allowed" : "Not Allowed"}
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-600 pl-1">
                          {ofm.phone && (
                            <p className="flex items-center gap-1.5">
                              <FiPhone className="text-gray-400" /> {ofm.phone}
                            </p>
                          )}
                          {ofm.email && (
                            <p className="flex items-center gap-1.5">
                              <FiMail className="text-gray-400" /> {ofm.email}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* ─── 11. REFERENCE MEDIA (Matching SupplierOrderDetailCard.jsx UI) ── */}
            {allMedia.length > 0 && (
              <SectionCard
                title={`Uploaded Reference Media (${allMedia.length})`}
                icon={<FiImage />}
                badge={<span className="text-xs text-gray-400">Physical stock verification</span>}
              >
                <div className="flex flex-wrap gap-3">
                  {allMedia.map((url, i) => (
                    <div
                      key={i}
                      className="group relative w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-2xl overflow-hidden border border-gray-200 hover:border-[#772037]/40 transition-all hover:shadow-md bg-gray-100"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setMediaModalIndex(i);
                          setMediaModalOpen(true);
                        }}
                        className="w-full h-full block cursor-pointer"
                      >
                        {isVideo(url) ? (
                          <div className="w-full h-full flex items-center justify-center bg-gray-900">
                            <span className="text-3xl">▶️</span>
                          </div>
                        ) : (
                          <img
                            src={url}
                            alt={`Reference ${i + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </button>

                      {/* Direct Download Button */}
                      <a
                        href={url}
                        download={`quotation-media-${i + 1}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Download Media"
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-lg bg-black/60 hover:bg-[#772037] text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FiDownload className="text-xs" />
                      </a>

                      {/* View/Play tag */}
                      <div className="absolute bottom-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                        <span className="bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-md font-semibold">
                          {isVideo(url) ? "Play" : "View"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* ─── 12. SPECIFICATIONS & BUYER Q&A ───────────────────────────── */}
            {quotation.specification?.relatedQuestionsAndAnswers?.length > 0 && (
              <SectionCard
                title={`Product Specifications & Buyer Q&A (${quotation.specification.relatedQuestionsAndAnswers.length})`}
                icon={<FiLayers />}
              >
                <div className="space-y-4">
                  {quotation.specification.relatedQuestionsAndAnswers.map((spec, i) => {
                    const isAgreed = spec.supplierAction?.isAgreed;
                    const proofs = Array.isArray(spec.supplierAction?.proof) ? spec.supplierAction.proof : [];
                    const thoughts = spec.supplierAction?.thoughts;

                    return (
                      <div key={i} className="p-4 rounded-2xl bg-gray-50/80 border border-gray-200 space-y-3 text-xs">
                        {/* Header: Question & Agreed Badge */}
                        <div className="flex items-start justify-between gap-3">
                          <div className="space-y-1">
                            <p className="font-bold text-gray-900 text-xs sm:text-sm">
                              Q: {spec.question || "Specification"}
                            </p>
                            <p className="text-gray-500 font-medium">
                              A: <strong className="text-gray-800">{spec.answeredByBuyer || spec.answer || "N/A"}</strong>
                            </p>
                          </div>
                          <span
                            className={`text-[10px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${isAgreed === "Yes"
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : isAgreed === "No"
                                ? "bg-red-50 text-red-700 border-red-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                              }`}
                          >
                            Agreed: {isAgreed === "Yes" ? "Yes" : isAgreed === "No" ? "No" : isAgreed || "Not Sure"}
                          </span>
                        </div>

                        {/* Supplier Note / Thoughts */}
                        {thoughts && (
                          <div className="bg-white p-3 rounded-xl border border-gray-200 space-y-1">
                            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                              Supplier Note / Thoughts
                            </p>
                            <p className="text-xs font-semibold text-gray-800 italic">
                              "{thoughts}"
                            </p>
                          </div>
                        )}

                        {/* Uploaded Proofs with ImageModal & Download */}
                        {proofs.length > 0 && (
                          <div className="pt-2 border-t border-gray-200/80 space-y-2">
                            <p className="text-[11px] font-bold text-gray-600 flex items-center gap-1.5">
                              <FiImage className="text-primaryColor text-xs" /> Uploaded Proof ({proofs.length})
                            </p>
                            <div className="flex flex-wrap gap-2.5">
                              {proofs.map((proofUrl, pIdx) => {
                                const isVid = isVideo(proofUrl);
                                return (
                                  <div
                                    key={pIdx}
                                    className="group relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border border-gray-200 hover:border-primaryColor/40 transition-all hover:shadow-md bg-gray-100 flex-shrink-0"
                                  >
                                    <button
                                      type="button"
                                      onClick={() => openProofModal(proofs, pIdx)}
                                      className="w-full h-full block cursor-pointer"
                                    >
                                      {isVid ? (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                          <span className="text-2xl">▶️</span>
                                        </div>
                                      ) : (
                                        <img
                                          src={proofUrl}
                                          alt={`Proof ${pIdx + 1}`}
                                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                      )}
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                    </button>

                                    {/* Direct Download Button */}
                                    <a
                                      href={proofUrl}
                                      download={`spec-proof-${pIdx + 1}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Download Proof"
                                      className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/60 hover:bg-primaryColor text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow text-xs"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <FiDownload />
                                    </a>

                                    {/* View tag */}
                                    <div className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                      <span className="bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold">
                                        {isVid ? "Play" : "View"}
                                      </span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </SectionCard>
            )}

            {/* ─── 13. OTHER BUYER REQUIREMENTS ────────────────────────────── */}
            {(quotation.specification?.otherRequirements?.length > 0 ||
              order?.specification?.otherRequirements?.some((req) => req.buyersRequirement && req.buyersRequirement !== "No")) && (
                <SectionCard
                  title="Other Buyer Requirements"
                  icon={<FiFileText />}
                >
                  <div className="space-y-4">
                    {(quotation.specification?.otherRequirements?.length > 0
                      ? quotation.specification.otherRequirements
                      : (order?.specification?.otherRequirements || []).filter((req) => req.buyersRequirement && req.buyersRequirement !== "No")
                    ).map((req, i) => {
                      const reqTitle = req.buyersRequirement || req.title || `Requirement #${i + 1}`;
                      const supplierResp =
                        req.supplierAction ||
                        quotation.specification?.otherRequirements?.find(
                          (sr) => sr.questionId === req.questionId || sr.buyersRequirement === req.buyersRequirement
                        )?.supplierAction;

                      const isAgreed = supplierResp?.isAgreed;
                      const thoughts = supplierResp?.thoughts;
                      const proofs = Array.isArray(supplierResp?.proof) ? supplierResp.proof : [];

                      return (
                        <div key={i} className="p-4 rounded-2xl bg-gray-50/80 border border-gray-200 space-y-3 text-xs">
                          {/* Header: Requirement & Agreed Badge */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="space-y-1">
                              <p className="font-bold text-gray-900 text-xs sm:text-sm">
                                {i + 1}. {reqTitle}
                              </p>
                            </div>
                            <span
                              className={`text-[10px] font-bold px-2.5 py-1 rounded-full border whitespace-nowrap ${isAgreed === "Yes"
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : isAgreed === "No"
                                  ? "bg-red-50 text-red-700 border-red-200"
                                  : "bg-amber-50 text-amber-700 border-amber-200"
                                }`}
                            >
                              Agreed: {isAgreed === "Yes" ? "Yes" : isAgreed === "No" ? "No" : isAgreed || "Not Sure"}
                            </span>
                          </div>

                          {/* Supplier Note / Thoughts */}
                          {thoughts && (
                            <div className="bg-white p-3 rounded-xl border border-gray-200 space-y-1">
                              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                                Supplier Note / Thoughts
                              </p>
                              <p className="text-xs font-semibold text-gray-800 italic">
                                "{thoughts}"
                              </p>
                            </div>
                          )}

                          {/* Uploaded Proofs with ImageModal & Download */}
                          {proofs.length > 0 && (
                            <div className="pt-2 border-t border-gray-200/80 space-y-2">
                              <p className="text-[11px] font-bold text-gray-600 flex items-center gap-1.5">
                                <FiImage className="text-primaryColor text-xs" /> Uploaded Proof ({proofs.length})
                              </p>
                              <div className="flex flex-wrap gap-2.5">
                                {proofs.map((proofUrl, pIdx) => {
                                  const isVid = isVideo(proofUrl);
                                  return (
                                    <div
                                      key={pIdx}
                                      className="group relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border border-gray-200 hover:border-primaryColor/40 transition-all hover:shadow-md bg-gray-100 flex-shrink-0"
                                    >
                                      <button
                                        type="button"
                                        onClick={() => openProofModal(proofs, pIdx)}
                                        className="w-full h-full block cursor-pointer"
                                      >
                                        {isVid ? (
                                          <div className="w-full h-full flex items-center justify-center bg-gray-900">
                                            <span className="text-2xl">▶️</span>
                                          </div>
                                        ) : (
                                          <img
                                            src={proofUrl}
                                            alt={`Proof ${pIdx + 1}`}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                          />
                                        )}
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                                      </button>

                                      {/* Direct Download Button */}
                                      <a
                                        href={proofUrl}
                                        download={`requirement-proof-${pIdx + 1}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        title="Download Proof"
                                        className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/60 hover:bg-primaryColor text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow text-xs"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <FiDownload />
                                      </a>

                                      {/* View tag */}
                                      <div className="absolute bottom-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                        <span className="bg-black/70 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold">
                                          {isVid ? "Play" : "View"}
                                        </span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </SectionCard>
              )}
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* 2. SIDEBAR COLUMN (lg:col-span-4)                                  */}
          {/* ═══════════════════════════════════════════════════════════════════ */}
          <div className="lg:col-span-4 space-y-6 sticky top-20">
            {/* Commercial Terms & Pricing Summary Card */}
            <div className="bg-white border border-primaryColor/20 rounded-2xl p-4 sm:p-5 shadow-sm space-y-4">
              <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                <h3 className="text-xs sm:text-sm font-extrabold text-primaryColor uppercase tracking-wide flex items-center gap-1.5">
                  <FiDollarSign className="text-base" /> Quotation Summary
                </h3>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  {resolvedStatus}
                </span>
              </div>

              {/* Price & Quantity Grid */}
              <div className="space-y-2.5 text-xs">
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Quoted Unit Price:</span>
                  <span className="font-extrabold text-base text-[#772037]">
                    ₹ {quotedAmount.toLocaleString()}
                    <span className="text-[10px] text-gray-400 font-normal"> / {orderUnit}</span>
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Quoted Volume:</span>
                  <span className="font-bold text-gray-900">
                    {quotedVolume.toLocaleString()} {orderUnit}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Batch / Lot:</span>
                  <span className="font-bold text-gray-800 truncate max-w-[140px]">
                    {quotation.batch_Lot_Number || quotation.batchNumber || "Standard"}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Holding Validity:</span>
                  <span className="font-bold text-emerald-600">
                    {validityDays > 0 ? `${validityDays} Days` : "—"}
                  </span>
                </div>
              </div>

              {/* Total Estimated Box */}
              {/* <div className="p-3.5 rounded-xl bg-primaryColor/10 border border-primaryColor/20 text-center">
                <p className="text-[10px] font-bold text-gray-500 uppercase tracking-tight">
                  Total Estimated Amount
                </p>
                <p className="text-xl sm:text-2xl font-extrabold text-primaryColor mt-0.5">
                  ₹ {Math.round(totalEstimatedAmount).toLocaleString("en-IN")}
                </p>
              </div> */}

              {/* Formal Declarations Checked */}
              {quotation.declarations?.length > 0 && (
                <div className="pt-3 border-t border-gray-100 space-y-1.5 text-xs">
                  <p className="font-bold text-gray-800 flex items-center gap-1">
                    <FiShield className="text-primaryColor text-sm" /> Formal Declarations Signed
                  </p>
                  {quotation.declarations.map((d, dIdx) => (
                    <div key={dIdx} className="flex items-start gap-1.5 text-[11px] text-gray-600">
                      <FiCheck className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      <span>{d.label || d.content}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Support Hotline */}
              <div className="pt-3 border-t border-gray-100 text-[11px] text-gray-400 flex items-center gap-2">
                <FiHelpCircle className="text-primaryColor text-sm flex-shrink-0" />
                <span>
                  Helpline:{" "}
                  <a href="tel:+9108062177433" className="text-primaryColor font-semibold hover:underline">
                    +9108062177433
                  </a>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── MODAL 1: ADVANCE CASH DISCOUNT APPROVAL / REJECTION ───────────── */}
      <PortalModal
        isOpen={advanceModalOpen}
        onClose={() => setAdvanceModalOpen(false)}
        title={advanceDecision ? "Accept Cash Discount" : "Decline Cash Discount"}
      >
        <div className="space-y-4 text-xs">
          <p className="text-gray-600 leading-relaxed">
            {advanceDecision
              ? `You are accepting the agreed cash discount of ₹${localAdvanceRequest?.cashDiscountAmount?.toLocaleString() || "0"} for this order. Please enter any final confirmation remarks below:`
              : "Please provide a brief reason for declining the cash discount terms:"}
          </p>

          <div className="space-y-1">
            <label className="font-bold text-gray-700">Remarks (Optional)</label>
            <textarea
              rows={3}
              value={advanceRemarks}
              onChange={(e) => setAdvanceRemarks(e.target.value)}
              placeholder="Enter remarks..."
              className="w-full p-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primaryColor/20 focus:border-primaryColor text-xs"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setAdvanceModalOpen(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={advanceSubmitting}
              onClick={handleAdvanceDecisionSubmit}
              className={`flex-1 py-2.5 rounded-xl font-bold text-white shadow-sm transition ${advanceDecision
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-red-600 hover:bg-red-700"
                }`}
            >
              {advanceSubmitting ? "Submitting..." : advanceDecision ? "Confirm Acceptance" : "Confirm Decline"}
            </button>
          </div>
        </div>
      </PortalModal>

      {/* ─── MODAL 2: INVOICE UPLOAD MODAL ──────────────────────────────────── */}
      <PortalModal
        isOpen={invoiceUploadModal.isOpen}
        onClose={() => setInvoiceUploadModal({ isOpen: false, type: "", title: "" })}
        title={`Upload ${invoiceUploadModal.title}`}
      >
        <div className="space-y-4 text-xs">
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-primaryColor/40 hover:border-primaryColor p-6 rounded-2xl cursor-pointer bg-primaryColor/[0.02] hover:bg-primaryColor/[0.05] transition text-center group">
            <input
              type="file"
              accept="application/pdf"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) setSelectedInvoiceFile(file);
              }}
            />
            <FiUploadCloud className="text-3xl text-primaryColor group-hover:scale-110 transition" />
            <span className="font-bold text-gray-800 mt-2 text-sm">
              {selectedInvoiceFile ? selectedInvoiceFile.name : "Select PDF Document"}
            </span>
            <span className="text-[10px] text-gray-400 mt-0.5">Maximum file size: 20MB</span>
          </label>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setInvoiceUploadModal({ isOpen: false, type: "", title: "" })}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!selectedInvoiceFile || uploadingInvoice}
              onClick={handleInvoiceUploadSubmit}
              className={`flex-1 py-2.5 rounded-xl font-bold text-white shadow-sm transition ${selectedInvoiceFile && !uploadingInvoice
                ? "bg-primaryColor hover:bg-primaryColor/90"
                : "bg-gray-300 cursor-not-allowed"
                }`}
            >
              {uploadingInvoice ? "Uploading to Cloud..." : "Upload & Save"}
            </button>
          </div>
        </div>
      </PortalModal>

      {/* ─── MODAL 3: STEP-WISE DISPATCH DECLARATION MODAL ────────────────── */}
      <PortalModal
        isOpen={dispatchConfirmModal}
        onClose={() => setDispatchConfirmModal(false)}
        title={`Supplier Dispatch Declaration (${typeKey === "Sample" ? "Sample Order" : "Full Order"})`}
      >
        <div className="space-y-4 text-xs">
          {/* Status Banner */}
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2.5 text-emerald-900">
            <FiShield className="text-emerald-600 text-base flex-shrink-0" />
            <span className="font-semibold leading-tight">
              Secure Digital Verification Enabled. Please review undertakings before dispatching lot.
            </span>
          </div>

          {/* Order Reference Card */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-500">Order Ref:</span>
              <span className="font-bold text-gray-800">{order?.orderNumber || "—"}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Quotation Ref:</span>
              <span className="font-bold text-gray-800">{quotation.quotationUniqueNumber}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Dispatch Type:</span>
              <span className="font-bold text-primaryColor">{typeKey.replace(/_/g, " ")}</span>
            </div>
          </div>

          {/* Required Undertakings Checklist */}
          <div className="space-y-2">
            <p className="font-extrabold text-gray-700 tracking-wider text-[11px] uppercase">
              Required Undertakings ({currentDeclarationPoints.length})
            </p>
            <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
              {currentDeclarationPoints.map((pt, idx) => (
                <div key={idx} className="p-2.5 bg-white border border-gray-200 rounded-xl flex items-start gap-2.5 shadow-2xs">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0 mt-0.5 font-bold">
                    <FiCheck className="text-xs" />
                  </div>
                  <p className="text-xs text-gray-700 font-medium leading-snug">{pt}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Digital Signature Seal */}
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl space-y-1.5">
            <div className="flex items-center gap-2 text-primaryColor font-bold text-xs">
              <FiFileText />
              <span>Digital Signature Seal</span>
            </div>
            <div className="p-2 bg-white rounded-lg border border-dashed border-gray-300 min-h-[50px] flex items-center justify-center">
              {user?.e_signature ? (
                <img src={user.e_signature} alt="E-Signature" className="h-10 object-contain" />
              ) : (
                <span className="font-serif italic font-bold text-gray-800 text-sm">
                  {user?.firstName ? `${user.firstName} ${user.lastName || ""}` : user?.name || "Authorized Supplier"}
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-500">
              <span>{user?.firstName ? `${user.firstName} ${user.lastName || ""}` : user?.name || "Supplier"}</span>
              <span className="text-emerald-600 font-bold flex items-center gap-1">
                <FiLock className="text-[10px]" /> Signed
              </span>
            </div>
          </div>

          {/* Agreement Checkbox */}
          <label className="flex items-start gap-3 p-3 bg-emerald-50/60 border border-emerald-200/80 rounded-xl cursor-pointer hover:bg-emerald-50 transition">
            <input
              type="checkbox"
              checked={declarationChecked}
              onChange={(e) => setDeclarationChecked(e.target.checked)}
              className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-xs text-gray-800 font-semibold leading-relaxed">
              I have carefully read, verified, and I agree to all the dispatch declaration points.
            </span>
          </label>

          {/* Modal Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setDispatchConfirmModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-gray-300 text-gray-600 font-bold hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!declarationChecked || dispatchingLoading}
              onClick={handleOpenOtpModal}
              className={`flex-1 py-2.5 rounded-xl font-bold text-white shadow-sm transition flex items-center justify-center gap-1.5 ${declarationChecked && !dispatchingLoading
                ? "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20"
                : "bg-gray-300 cursor-not-allowed"
                }`}
            >
              Confirm & Get OTP
            </button>
          </div>
        </div>
      </PortalModal>

      {/* ─── MODAL 4: COMMON OTP ENTERING MODAL ─────────────────────────────── */}
      <OTPModal
        show={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerified={handleOtpVerified}
        identifier={user?.phone || user?.email || ""}
      />

      {/* ─── MODAL 4: MEDIA VIEWER / DOWNLOAD (matching SupplierOrderDetailCard.jsx) ── */}
      {mediaModalOpen && (
        <ImageModal
          media={activeMediaList.length > 0 ? activeMediaList : allMedia}
          initialIndex={mediaModalIndex}
          onClose={() => {
            setMediaModalOpen(false);
            setActiveMediaList([]);
          }}
        />
      )}
    </section>
  );
};

export default SupplierQuotationDetails;
